import './globals.css'
import './i18n'

import { axiosInstance } from '@jobilla/nexus-sdk/lib/client'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { createRoot } from 'react-dom/client'
import { routeTree } from './routeTree.gen'

axiosInstance.defaults.baseURL = import.meta.env.VITE_NEXUS_API_URL

// A Sentry DSN is a public client-side identifier (it ships in the browser bundle), not a secret,
// so we commit meet's own here rather than read it from a Doppler config.
const SENTRY_DSN = 'https://1f6755431a890924e7dae10080f6a9f4@o4509242152386560.ingest.de.sentry.io/4511694488666193'

if (SENTRY_DSN) {
	Sentry.init({
		dsn: SENTRY_DSN,
		environment: import.meta.env.VITE_APP_ENV,
		enabled: import.meta.env.PROD && !import.meta.env.VITE_E2E,
		integrations: [
			Sentry.thirdPartyErrorFilterIntegration({
				// Must match applicationKey in vite.config.mts
				filterKeys: ['kone-meet'],
				behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
			}),
		],
		beforeSend(event) {
			// thirdPartyErrorFilterIntegration only classifies events that have frames.
			const exceptionValues = event.exception?.values

			if (exceptionValues?.length && exceptionValues.every(value => !value.stacktrace?.frames?.length)) {
				event.tags = { ...event.tags, no_stack_frames: true }
			}

			return event
		},
	})
}

const queryClient = new QueryClient({
	// Under E2E the network is MSW-mocked and deterministic, so retries only add
	// latency and make error-state assertions flaky/slow. Disable them there.
	defaultOptions: import.meta.env.VITE_E2E
		? { queries: { retry: false }, mutations: { retry: false } }
		: undefined,
})

const router = createRouter({
	routeTree,
	context: { queryClient },
	defaultPreload: 'intent',
	defaultPreloadStaleTime: 0,
	scrollRestoration: true,
})

declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}

createRoot(document.getElementById('root')!).render(
	<QueryClientProvider client={queryClient}>
		<RouterProvider router={router} />
		<ReactQueryDevtools initialIsOpen={false} />
	</QueryClientProvider>,
)
